import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Eye, Copy, Wallet, Monitor, Globe, AlertTriangle, 
  Clock, Fingerprint, MapPin, Shield
} from "lucide-react";
import { toast } from "@/hooks/use-toast";

type FraudIdentity = {
  id: string;
  status: string;
  strikes: number;
  risk_score: number;
  first_seen: string;
  last_seen: string;
  ban_reason: string | null;
  ban_at: string | null;
  notes: string | null;
  wallets?: Array<{
    wallet_address: string;
    confidence: number;
    is_primary: boolean;
    first_seen: string;
    last_seen: string;
  }>;
};

type DeviceFingerprint = {
  id: string;
  device_hash: string;
  user_agent: string | null;
  platform: string | null;
  screen_resolution: string | null;
  webgl_renderer: string | null;
  canvas_hash: string | null;
  hardware_concurrency: number | null;
  device_memory: number | null;
  confidence: number;
  first_seen: string;
  last_seen: string;
};

type IPTracking = {
  id: string;
  ip_address: string;
  country: string | null;
  is_vpn: boolean | null;
  is_tor: boolean | null;
  is_datacenter: boolean | null;
  hit_count: number;
  confidence: number;
  first_seen: string;
  last_seen: string;
};

type BehaviorSignal = {
  id: string;
  signal_type: string;
  weight: number;
  details: any;
  detected_at: string;
};

type AntifraudLog = {
  id: string;
  event_type: string;
  decision: string;
  risk_score: number;
  risk_factors: any;
  counterparty_wallet: string | null;
  device_hash: string | null;
  ip_address: string | null;
  price: number | null;
  created_at: string;
};

interface Props {
  fraud: FraudIdentity;
}

export function FraudIdentityDetailDialog({ fraud }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState<DeviceFingerprint[]>([]);
  const [ips, setIps] = useState<IPTracking[]>([]);
  const [signals, setSignals] = useState<BehaviorSignal[]>([]);
  const [logs, setLogs] = useState<AntifraudLog[]>([]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: text.slice(0, 30) + "..." });
  };

  const fetchDetails = async () => {
    if (!open) return;
    setLoading(true);

    try {
      // Fetch device fingerprints
      const { data: deviceData } = await supabase
        .from("fraud_device_fingerprints")
        .select("*")
        .eq("fraud_identity_id", fraud.id)
        .order("last_seen", { ascending: false });
      setDevices(deviceData || []);

      // Fetch IP tracking
      const { data: ipData } = await supabase
        .from("fraud_ip_tracking")
        .select("*")
        .eq("fraud_identity_id", fraud.id)
        .order("last_seen", { ascending: false });
      setIps(ipData || []);

      // Fetch behavior signals
      const { data: signalData } = await supabase
        .from("fraud_behavior_signals")
        .select("*")
        .eq("fraud_identity_id", fraud.id)
        .order("detected_at", { ascending: false });
      setSignals(signalData || []);

      // Fetch antifraud logs for all linked wallets
      const walletAddresses = fraud.wallets?.map(w => w.wallet_address) || [];
      if (walletAddresses.length > 0) {
        const { data: logData } = await supabase
          .from("antifraud_logs")
          .select("*")
          .in("wallet_address", walletAddresses)
          .order("created_at", { ascending: false })
          .limit(50);
        setLogs(logData || []);
      }
    } catch (error) {
      console.error("Error fetching fraud details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchDetails();
    }
  }, [open, fraud.id]);

  const getSignalIcon = (type: string) => {
    switch (type) {
      case 'self_trade':
      case 'wash_trade':
        return '🔄';
      case 'price_manipulation':
        return '💰';
      case 'rapid_trades':
        return '⚡';
      case 'disposable_wallet':
        return '🗑️';
      case 'cyclic_trading':
        return '🔁';
      case 'vpn_detected':
      case 'tor_detected':
        return '🕵️';
      default:
        return '⚠️';
    }
  };

  const getSignalLabel = (type: string) => {
    const labels: Record<string, string> = {
      'self_trade': 'Auto-trading',
      'wash_trade': 'Wash Trading',
      'price_manipulation': 'Manipolazione Prezzo',
      'rapid_trades': 'Trading Rapido',
      'disposable_wallet': 'Wallet Usa e Getta',
      'cyclic_trading': 'Trading Ciclico',
      'vpn_detected': 'VPN Rilevato',
      'tor_detected': 'Tor Rilevato',
      'boost_artificial': 'Boost Artificiale'
    };
    return labels[type] || type;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Eye className="w-3 h-3 mr-1" />
          Dettagli
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Dettagli Identità Fraudolenta
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[70vh] pr-4">
          {/* Header Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">ID</p>
              <p className="font-mono text-xs truncate">{fraud.id.slice(0, 12)}...</p>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">Stato</p>
              <Badge 
                variant={fraud.status === 'banned' ? 'destructive' : fraud.status === 'restricted' ? 'secondary' : 'default'}
                className={fraud.status === 'warned' ? 'bg-orange-500/20 text-orange-500' : ''}
              >
                {fraud.status}
              </Badge>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">Strike</p>
              <p className={`font-bold ${fraud.strikes >= 3 ? 'text-destructive' : fraud.strikes >= 2 ? 'text-yellow-500' : ''}`}>
                {fraud.strikes}/3
              </p>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">Rischio</p>
              <div className="flex items-center gap-2">
                <div className="w-12 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${fraud.risk_score >= 70 ? 'bg-destructive' : fraud.risk_score >= 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
                    style={{ width: `${fraud.risk_score}%` }}
                  />
                </div>
                <span className="text-sm font-bold">{fraud.risk_score}</span>
              </div>
            </div>
          </div>

          {fraud.ban_reason && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
              <p className="text-xs font-semibold text-destructive mb-1">Motivo Ban</p>
              <p className="text-sm">{fraud.ban_reason}</p>
              {fraud.ban_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Bannato il: {new Date(fraud.ban_at).toLocaleString('it-IT')}
                </p>
              )}
            </div>
          )}

          <Tabs defaultValue="wallets" className="w-full">
            <TabsList className="w-full grid grid-cols-5 mb-4">
              <TabsTrigger value="wallets" className="text-xs">
                <Wallet className="w-3 h-3 mr-1" />
                Wallet ({fraud.wallets?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="devices" className="text-xs">
                <Monitor className="w-3 h-3 mr-1" />
                Dispositivi ({devices.length})
              </TabsTrigger>
              <TabsTrigger value="ips" className="text-xs">
                <Globe className="w-3 h-3 mr-1" />
                IP ({ips.length})
              </TabsTrigger>
              <TabsTrigger value="signals" className="text-xs">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Segnali ({signals.length})
              </TabsTrigger>
              <TabsTrigger value="logs" className="text-xs">
                <Clock className="w-3 h-3 mr-1" />
                Log ({logs.length})
              </TabsTrigger>
            </TabsList>

            {/* Wallets Tab */}
            <TabsContent value="wallets" className="space-y-2">
              {fraud.wallets?.map((w) => (
                <div key={w.wallet_address} className="p-3 bg-muted/30 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs break-all">{w.wallet_address}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(w.wallet_address)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex gap-1">
                      {w.is_primary && <Badge>Principale</Badge>}
                      <Badge variant="outline">{w.confidence}%</Badge>
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>Prima attività: {new Date(w.first_seen).toLocaleDateString('it-IT')}</span>
                    <span>Ultima: {new Date(w.last_seen).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
              ))}
              {!fraud.wallets?.length && (
                <p className="text-center text-muted-foreground py-4">Nessun wallet collegato</p>
              )}
            </TabsContent>

            {/* Devices Tab */}
            <TabsContent value="devices" className="space-y-2">
              {loading ? (
                <p className="text-center text-muted-foreground py-4">Caricamento...</p>
              ) : devices.length > 0 ? (
                devices.map((d) => (
                  <div key={d.id} className="p-3 bg-muted/30 rounded-lg border space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Fingerprint className="w-4 h-4 text-muted-foreground" />
                        <span className="font-mono text-xs">{d.device_hash.slice(0, 16)}...</span>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(d.device_hash)}>
                          <Copy className="w-2.5 h-2.5" />
                        </Button>
                      </div>
                      <Badge variant="outline">{d.confidence}% conf</Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                      {d.platform && <span>📱 {d.platform}</span>}
                      {d.screen_resolution && <span>🖥️ {d.screen_resolution}</span>}
                      {d.hardware_concurrency && <span>⚙️ {d.hardware_concurrency} cores</span>}
                      {d.device_memory && <span>💾 {d.device_memory}GB RAM</span>}
                    </div>
                    {d.user_agent && (
                      <p className="text-[10px] text-muted-foreground truncate">{d.user_agent}</p>
                    )}
                    {d.webgl_renderer && (
                      <p className="text-[10px] text-muted-foreground">GPU: {d.webgl_renderer}</p>
                    )}
                    <div className="flex gap-4 text-[10px] text-muted-foreground border-t pt-2">
                      <span>Prima: {new Date(d.first_seen).toLocaleDateString('it-IT')}</span>
                      <span>Ultima: {new Date(d.last_seen).toLocaleDateString('it-IT')}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-4">Nessun dispositivo registrato</p>
              )}
            </TabsContent>

            {/* IPs Tab */}
            <TabsContent value="ips" className="space-y-2">
              {loading ? (
                <p className="text-center text-muted-foreground py-4">Caricamento...</p>
              ) : ips.length > 0 ? (
                ips.map((ip) => (
                  <div key={ip.id} className="p-3 bg-muted/30 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span className="font-mono text-sm">{ip.ip_address}</span>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(ip.ip_address)}>
                          <Copy className="w-2.5 h-2.5" />
                        </Button>
                        {ip.country && <Badge variant="secondary">{ip.country}</Badge>}
                      </div>
                      <span className="text-xs text-muted-foreground">{ip.hit_count} hits</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {ip.is_vpn && <Badge variant="destructive" className="text-[10px]">VPN</Badge>}
                      {ip.is_tor && <Badge variant="destructive" className="text-[10px]">TOR</Badge>}
                      {ip.is_datacenter && <Badge variant="secondary" className="text-[10px]">Datacenter</Badge>}
                      <Badge variant="outline" className="text-[10px]">{ip.confidence}% conf</Badge>
                    </div>
                    <div className="flex gap-4 text-[10px] text-muted-foreground mt-2">
                      <span>Prima: {new Date(ip.first_seen).toLocaleDateString('it-IT')}</span>
                      <span>Ultima: {new Date(ip.last_seen).toLocaleDateString('it-IT')}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-4">Nessun IP registrato</p>
              )}
            </TabsContent>

            {/* Signals Tab */}
            <TabsContent value="signals" className="space-y-2">
              {loading ? (
                <p className="text-center text-muted-foreground py-4">Caricamento...</p>
              ) : signals.length > 0 ? (
                signals.map((s) => (
                  <div key={s.id} className="p-3 bg-muted/30 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getSignalIcon(s.signal_type)}</span>
                        <span className="font-medium text-sm">{getSignalLabel(s.signal_type)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={s.weight >= 30 ? 'destructive' : s.weight >= 15 ? 'secondary' : 'outline'}>
                          +{s.weight} punti
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(s.detected_at).toLocaleString('it-IT')}
                        </span>
                      </div>
                    </div>
                    {s.details && (
                      <pre className="text-[10px] bg-muted p-2 rounded overflow-auto max-h-20">
                        {JSON.stringify(s.details, null, 2)}
                      </pre>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-4">Nessun segnale comportamentale</p>
              )}
            </TabsContent>

            {/* Logs Tab */}
            <TabsContent value="logs" className="space-y-2">
              {loading ? (
                <p className="text-center text-muted-foreground py-4">Caricamento...</p>
              ) : logs.length > 0 ? (
                logs.map((log) => (
                  <div key={log.id} className="p-3 bg-muted/30 rounded-lg border text-xs">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={log.decision === 'blocked' ? 'destructive' : log.decision === 'flagged' ? 'secondary' : 'outline'}>
                          {log.decision}
                        </Badge>
                        <span className="font-medium">{log.event_type}</span>
                      </div>
                      <span className="text-muted-foreground">
                        {new Date(log.created_at).toLocaleString('it-IT')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                      <span>Rischio: {log.risk_score}</span>
                      {log.price && <span>Prezzo: ${log.price.toFixed(2)}</span>}
                      {log.counterparty_wallet && (
                        <span className="font-mono truncate col-span-2">
                          Controparte: {log.counterparty_wallet.slice(0, 8)}...{log.counterparty_wallet.slice(-4)}
                        </span>
                      )}
                    </div>
                    {log.risk_factors && Array.isArray(log.risk_factors) && log.risk_factors.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-2">
                        {log.risk_factors.map((factor: unknown, i: number) => (
                          <Badge key={i} variant="outline" className="text-[10px]">
                            {String(factor)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-4">Nessun log antifrode</p>
              )}
            </TabsContent>
          </Tabs>

          {/* Footer Info */}
          <div className="mt-4 pt-4 border-t text-xs text-muted-foreground flex justify-between">
            <span>Prima attività: {fraud.first_seen ? new Date(fraud.first_seen).toLocaleString('it-IT') : '-'}</span>
            <span>Ultima attività: {fraud.last_seen ? new Date(fraud.last_seen).toLocaleString('it-IT') : '-'}</span>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
